import {useRef, useState} from 'react'
import {useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Hint} from '@/components/ui/hint'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {ActionEvidenceBlock} from '@/components/action-evidence-block'
import {ActionNotesThread} from '@/components/action-notes-thread'
import {PageHeading} from '@/components/page-heading'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {ListEmptyState, SectionCard} from '@/components/section-card'
import {MicroLabel} from '@/components/period-selector'
import {TargetDateMeta} from '@/components/target-date-meta'
import {useAuth} from '@/hooks/platform/use-auth'
import {uploadToStorage} from '@/hooks/sgsst/evidencias/use-evidence'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  mintMemberActionUploadTarget,
  rutaDeDescargaDeMiAccion,
  useAdvanceAction,
  useAddMemberActionNote,
  useAttachMemberActionEvidence,
  useDeleteMemberActionNote,
  useMarcarMiPlanEjecucion,
  MEMBER_PAGE_SIZE,
  useMemberAcciones,
  useMemberPlanTrabajo,
  type MemberAction,
  type MemberPlanActividad,
  type SgsstActionStatus,
} from '@/hooks/empresa/mi_espacio/use-member'
import {translateApiError} from '@/lib/errors'
import {formatShortDate, localToday} from '@/lib/risk'
import {cn} from '@/lib/utils'
import {documentoCorto} from '@/lib/documento'

const STATUS_ORDER: SgsstActionStatus[] = ['pendiente', 'en_curso', 'cumplida']

const STATUS_TONE: Record<SgsstActionStatus, 'neutral' | 'amber' | 'green'> = {
  pendiente: 'neutral',
  en_curso: 'amber',
  cumplida: 'green',
}

const ACTIVE_SEG: Record<SgsstActionStatus, string> = {
  pendiente: 'bg-card text-foreground',
  en_curso: 'tone-amber',
  cumplida: 'tone-green',
}

/** No reusa `SegmentedControl`: este solo avanza y se pinta con el color del estado. */
function EstadoControl({action, businessId}: {action: MemberAction; businessId: string}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const advance = useAdvanceAction(businessId)
  const [error, setError] = useState<string | null>(null)
  const currentIdx = STATUS_ORDER.indexOf(action.status)
  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <MicroLabel>{t('member.acciones.estadoLabel')}</MicroLabel>
        <TraceLine action={action} />
      </div>
      <div
        className="flex w-full gap-0.5 rounded-lg bg-secondary p-0.5 sm:w-fit"
        role="group"
        aria-label={t('member.acciones.estadoLabel')}
        data-testid="estado-seg">
        {STATUS_ORDER.map((status, idx) => {
          const isCurrent = idx === currentIdx
          // Solo avanza: la persona nunca revierte su propio trabajo.
          const disabled = idx <= currentIdx || advance.isPending || godReadOnly
          return (
            <button
              key={status}
              type="button"
              data-testid={`estado-btn-${status}`}
              aria-pressed={isCurrent}
              disabled={disabled}
              onClick={() => {
                setError(null)
                advance.mutate({actionId: action.id, status}, {onError: (err) => setError(translateApiError(err))})
              }}
              className={cn(
                'flex-1 rounded-md px-3 py-2 text-[12.5px] font-medium whitespace-nowrap transition-colors sm:flex-none sm:py-1.5',
                isCurrent ? `${ACTIVE_SEG[status]} shadow-xs` : 'text-muted-foreground',
                !isCurrent && !disabled && 'hover:bg-card hover:text-foreground',
                !isCurrent && idx < currentIdx && 'opacity-50'
              )}>
              {t(`plan.status.${status}`)}
            </button>
          )
        })}
      </div>
      {error && (
        <p className="text-xs text-destructive" data-testid="estado-error">
          {error}
        </p>
      )}
    </div>
  )
}

function TraceLine({action}: {action: MemberAction}) {
  const {t} = useTranslation()
  const actor = action.status_changed_by_me ? t('member.byYou') : t('member.byCompany')
  let text: string | null = null
  if (action.status === 'cumplida' && action.completed_at) {
    text = `${t('member.acciones.cumplidaOn', {date: formatShortDate(action.completed_at.slice(0, 10))})} ${actor}`
  } else if (action.status === 'en_curso' && action.status_changed_at) {
    text = `${t('member.acciones.enCursoSince', {date: formatShortDate(action.status_changed_at.slice(0, 10))})} ${actor}`
  }
  if (!text) return null
  return (
    <span className="font-mono text-[11.5px] text-muted-foreground" data-testid="estado-trace">
      {text}
    </span>
  )
}

const EFICACIA_TONE: Record<'eficaz' | 'no_eficaz', 'green' | 'red'> = {
  eficaz: 'green',
  no_eficaz: 'red',
}

function EficaciaBanda({action}: {action: MemberAction}) {
  const {t} = useTranslation()
  if (!action.eficacia) return null
  const eficaz = action.eficacia === 'eficaz'
  return (
    <div className="space-y-2" data-testid="member-eficacia">
      <MicroLabel>{t('member.acciones.eficaciaLabel')}</MicroLabel>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Tag tone={EFICACIA_TONE[action.eficacia]} data-testid="member-eficacia-tag">
          {t(eficaz ? 'member.acciones.eficaciaEficaz' : 'member.acciones.eficaciaNoEficaz')}
        </Tag>
        {action.eficacia_verificada_at && (
          <span className="font-mono text-[11.5px] text-muted-foreground" data-testid="member-eficacia-fecha">
            {t('member.acciones.eficaciaVerifiedOn', {
              date: formatShortDate(action.eficacia_verificada_at.slice(0, 10)),
            })}
          </span>
        )}
      </div>
      {!eficaz && <Hint>{t('member.acciones.eficaciaNoEficazHelp')}</Hint>}

      {action.eficacia_nota && (
        <div>
          <MicroLabel>{t('member.acciones.eficaciaNotaLabel')}</MicroLabel>
          <p className="mt-1 text-[13.5px] text-foreground" data-testid="member-eficacia-nota">
            {action.eficacia_nota}
          </p>
        </div>
      )}
    </div>
  )
}

/** La segunda lectura de IA sigue solo el interruptor `ai_validate_aportes` de la empresa, nunca uno por archivo. */
function NotesAndEvidence({action, businessId}: {action: MemberAction; businessId: string}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const addNote = useAddMemberActionNote(businessId, action.id)
  const deleteNote = useDeleteMemberActionNote(businessId, action.id)
  const attach = useAttachMemberActionEvidence(businessId, action.id)

  async function upload(file: File, _validateAi: boolean, onProgress: (pct: number) => void) {
    const target = await mintMemberActionUploadTarget(businessId, action.id, file)
    await uploadToStorage(target, file, onProgress)
    await attach.mutateAsync({objectKey: target.object_key, file})
  }

  return (
    <div className="space-y-4">
      <div>
        <MicroLabel>{t('nota.notesTitle')}</MicroLabel>
        <div className="mt-2">
          <ActionNotesThread
            notes={action.notes}
            editable={!godReadOnly}
            adding={addNote.isPending}
            deleteNote={deleteNote}
            onAdd={(text) => addNote.mutateAsync(text)}
          />
        </div>
      </div>
      {action.standard_code && (
        <div>
          <MicroLabel>{t('nota.evidenceTitle')}</MicroLabel>
          <div className="mt-2">
            <ActionEvidenceBlock
              businessId={businessId}
              rutaDeDescarga={(evidenceId) => rutaDeDescargaDeMiAccion(businessId, action.id, evidenceId)}
              items={action.evidence}
              editable={!godReadOnly}
              showAiToggle={false}
              onUpload={upload}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function ActionCard({action, businessId}: {action: MemberAction; businessId: string}) {
  const {t} = useTranslation()
  const {user} = useAuth()
  // nosemgrep: browser-clock-business-date -- overdue determination off the browser clock; tracked debt, not fixed in this wave (FE-02)
  const overdue = action.status !== 'cumplida' && action.target_date < localToday()
  return (
    <SectionCard
      testId="member-action"
      title={action.description}
      subtitle={
        action.standard_code && (
          <span className="flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-xs font-semibold text-primary">{action.standard_code}</span>
            {action.standard_name}
          </span>
        )
      }
      actions={<Tag tone={STATUS_TONE[action.status]}>{t(`plan.status.${action.status}`)}</Tag>}
      bodyClassName="p-0">
      <div className="grid gap-4 px-5 py-4 sm:grid-cols-3">
        <div>
          <MicroLabel>{t('plan.meta.responsible')}</MicroLabel>
          <p className="mt-1.5">
            <span className="inline-flex items-center rounded-full border border-input px-2.5 py-0.5 text-[12.5px] font-medium">
              {user?.full_name} · {t('member.you')}
            </span>
          </p>
          {user?.cedula && (
            <p className="mt-1 font-mono text-[11px] text-muted-foreground">
              {documentoCorto(user.tipo_documento, user.cedula)}
            </p>
          )}
        </div>
        <div>
          <MicroLabel>{t('plan.meta.resources')}</MicroLabel>
          <p className="mt-1.5 text-[13.5px] text-muted-foreground">{action.resources ?? '—'}</p>
        </div>
        <TargetDateMeta
          targetDate={action.target_date}
          previousTargetDate={action.previous_target_date ?? null}
          overdue={overdue}
        />
      </div>

      <div className="surface-divider border-t px-5 py-4">
        <EstadoControl action={action} businessId={businessId} />
      </div>
      {action.eficacia && (
        <div className="surface-divider border-t px-5 py-4">
          <EficaciaBanda action={action} />
        </div>
      )}
      <div className="surface-divider border-t px-5 py-4">
        <NotesAndEvidence action={action} businessId={businessId} />
      </div>
    </SectionCard>
  )
}

const MESES_PLAN = ['E', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']

function PlanTrabajoCard({actividad, businessId}: {actividad: MemberPlanActividad; businessId: string}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const marcar = useMarcarMiPlanEjecucion(businessId)
  const [error, setError] = useState<string | null>(null)
  // nosemgrep: browser-clock-business-date -- current-month determination off the browser clock; tracked debt, not fixed in this wave (FE-02)
  const mesActual = new Date().getMonth() + 1
  const ejecutados = new Set(actividad.meses_ejecutados)
  const planeados = new Set(actividad.meses_planeados)
  const yaEjecutado = ejecutados.has(mesActual)
  const planeadoEsteMes = planeados.has(mesActual)
  return (
    <SectionCard
      testId="member-plan-actividad"
      title={actividad.titulo}
      subtitle={actividad.meta}
      actions={
        yaEjecutado ? (
          <Tag tone="green" data-testid="plan-actividad-hecha">
            {t('member.plan.hechaEsteMes')}
          </Tag>
        ) : undefined
      }>
      {actividad.cita && <p className="font-mono text-[11px] text-muted-foreground">{actividad.cita}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <MicroLabel>{t('member.plan.meses')}</MicroLabel>
        <span className="font-mono text-[12px] tabular-nums text-foreground">
          {actividad.meses_planeados
            .map((m) => (ejecutados.has(m) ? `${MESES_PLAN[m - 1]}✓` : MESES_PLAN[m - 1]))
            .join(' · ')}
        </span>
      </div>
      {!yaEjecutado && (
        <div className="mt-4">
          <Button
            size="sm"
            className="w-full sm:w-auto"
            data-testid="plan-actividad-marcar"
            disabled={!planeadoEsteMes || marcar.isPending || godReadOnly}
            onClick={() => {
              setError(null)
              marcar.mutate(
                {actividadId: actividad.id, mes: mesActual},
                {onError: (err) => setError(translateApiError(err))}
              )
            }}>
            {planeadoEsteMes ? t('member.plan.marcar') : t('member.plan.noEsteMes')}
          </Button>
          {error && (
            <p className="mt-1.5 text-xs text-destructive" data-testid="plan-actividad-error">
              {error}
            </p>
          )}
        </div>
      )}
    </SectionCard>
  )
}

const MIS_TABS = ['mejoramiento', 'plan'] as const
type MisTab = (typeof MIS_TABS)[number]

function ChipConteo({n}: {n: number}) {
  if (n === 0) return null
  return <span className="ml-1.5 rounded-full bg-secondary px-1.5 font-mono text-[10px] text-foreground">{n}</span>
}

export function MiAccionesPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const [accionesPage, setAccionesPage] = useState(1)
  const [planPage, setPlanPage] = useState(1)
  const {data: acciones, isLoading} = useMemberAcciones(businessId, accionesPage)
  const {data: planActividades} = useMemberPlanTrabajo(businessId, planPage)
  const [searchParams, setSearchParams] = useSearchParams()

  const accionesTotal = acciones?.total ?? 0
  const planTotal = planActividades?.total ?? 0
  const sinNada = accionesTotal === 0 && planTotal === 0
  // «Vencida» es fecha de negocio y el reloj del navegador no es la fuente, así que gana «Mejoramiento» si
  // tiene algo. Se decide una sola vez: recalculada, un refetch al recuperar el foco movía la pestaña.
  const porDefectoRef = useRef<MisTab | null>(null)
  if (porDefectoRef.current === null && acciones && planActividades) {
    porDefectoRef.current = accionesTotal > 0 ? 'mejoramiento' : 'plan'
  }
  const porDefecto: MisTab = porDefectoRef.current ?? 'mejoramiento'
  const tabParam = searchParams.get('tab')
  const tab: MisTab = MIS_TABS.includes(tabParam as MisTab) ? (tabParam as MisTab) : porDefecto
  function setTab(next: MisTab) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }

  return (
    <div className="space-y-5">
      <PageHeading title={t('member.acciones.title')} />

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : sinNada ? (
        <div className="surface mx-auto max-w-2xl">
          <ListEmptyState page title={t('member.acciones.emptyTitle')} />
        </div>
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as MisTab)}>
          <TabsList data-testid="mis-acciones-tabs">
            <TabsTrigger value="mejoramiento" data-testid="mis-acciones-tab-mejoramiento">
              {t('member.acciones.tabs.mejoramiento')}
              <ChipConteo n={accionesTotal} />
            </TabsTrigger>
            <TabsTrigger value="plan" data-testid="mis-acciones-tab-plan">
              {t('member.acciones.tabs.plan')}
              <ChipConteo n={planTotal} />
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mejoramiento" className="pt-4">
            {accionesTotal === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="mis-acciones-mejoramiento-vacio">
                {t('member.acciones.tabs.mejoramientoVacio')}
              </p>
            ) : (
              <section className="space-y-4" data-testid="member-acciones-plan">
                {(acciones?.items ?? []).map((action) => (
                  <ActionCard key={action.id} action={action} businessId={businessId!} />
                ))}
                <PaginationBar
                  page={accionesPage}
                  pageSize={MEMBER_PAGE_SIZE}
                  total={accionesTotal}
                  onPageChange={setAccionesPage}
                />
              </section>
            )}
          </TabsContent>

          <TabsContent value="plan" className="pt-4">
            {planTotal === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="mis-acciones-plan-vacio">
                {t('member.acciones.tabs.planVacio')}
              </p>
            ) : (
              <section className="space-y-4" data-testid="member-plan-trabajo">
                {(planActividades?.items ?? []).map((actividad) => (
                  <PlanTrabajoCard key={actividad.id} actividad={actividad} businessId={businessId!} />
                ))}
                <PaginationBar
                  page={planPage}
                  pageSize={MEMBER_PAGE_SIZE}
                  total={planTotal}
                  onPageChange={setPlanPage}
                />
              </section>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}
